import { createSignal } from "solid-js";

/**
 * Debug utilities for `/alerts?debug=alerts` diagnostics.
 * Captures auth, permission, /me resource, alerts query, API, and render loop signals.
 */

interface DebugEvent {
  timestamp: number;
  category: string;
  message: string;
  data?: Record<string, unknown>;
}

const [debugEnabled, setDebugEnabled] = createSignal(false);
const debugEvents: DebugEvent[] = [];
const maxEvents = 100;
const renderCounts: Record<string, number> = {};
let watchdogLastHeartbeat = 0;
let watchdogBlockedCount = 0;
let watchdogStarted = false;
let globalCaptureInstalled = false;

function getDebugFlag(): boolean {
  if (typeof window === "undefined") return false;
  const params = new URLSearchParams(window.location.search);
  const flag = params.get("debug");
  if (flag === "off") {
    localStorage.removeItem("conatus.debug.alerts");
    return false;
  }
  if (flag === "alerts") {
    localStorage.setItem("conatus.debug.alerts", "1");
    return true;
  }
  return localStorage.getItem("conatus.debug.alerts") === "1";
}

export function initDebug(): void {
  const enabled = getDebugFlag();
  setDebugEnabled(enabled);
  if (enabled) {
    console.info("[debug] Alerts debug mode enabled. Use /alerts?debug=off to disable.");
    startWatchdog();
    installGlobalCapture();
  }
}

export function isDebugEnabled(): boolean {
  return debugEnabled();
}

export function recordDebugEvent(
  category: string,
  message: string,
  data?: Record<string, unknown>,
): void {
  if (!debugEnabled()) return;
  const event: DebugEvent = {
    timestamp: Date.now(),
    category,
    message,
    data,
  };
  debugEvents.push(event);
  if (debugEvents.length > maxEvents) {
    debugEvents.shift();
  }
  console.info(`[${category}] ${message}`, data ?? "");
}

export function recordRender(componentName: string): void {
  if (!debugEnabled()) return;
  renderCounts[componentName] = (renderCounts[componentName] ?? 0) + 1;
  const count = renderCounts[componentName];
  if (count === 1) {
    recordDebugEvent("render", `${componentName} mounted`);
  } else if (count % 50 === 0) {
    recordDebugEvent("render", `${componentName} render count: ${count}`);
  }
  if (count > 200) {
    recordDebugEvent("render-storm", `${componentName} exceeded 200 renders`);
  }
}

function startWatchdog(): void {
  if (watchdogStarted) return;
  watchdogStarted = true;
  watchdogLastHeartbeat = performance.now();
  setInterval(() => {
    const now = performance.now();
    const drift = now - watchdogLastHeartbeat - 500;
    watchdogLastHeartbeat = now;
    if (drift > 2000) {
      watchdogBlockedCount++;
      recordDebugEvent(
        "watchdog",
        `Main thread blocked: ${drift.toFixed(0)}ms drift`,
        { blockCount: watchdogBlockedCount },
      );
    }
    if (drift > 5000) {
      recordDebugEvent(
        "watchdog-critical",
        `MAIN THREAD SEVERELY BLOCKED: ${drift.toFixed(0)}ms`,
      );
    }
  }, 500);
}

function installGlobalCapture(): void {
  if (globalCaptureInstalled || typeof window === "undefined") return;
  globalCaptureInstalled = true;
  window.addEventListener("error", (event) => {
    recordDebugEvent("global-error", "Uncaught error", {
      message: event.message,
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
    });
  });
  window.addEventListener("unhandledrejection", (event) => {
    recordDebugEvent("global-rejection", "Unhandled promise rejection", {
      reason: event.reason instanceof Error ? event.reason.message : String(event.reason),
    });
  });
}

export function getDebugEvents(): DebugEvent[] {
  return [...debugEvents];
}

export function getRenderCounts(): Record<string, number> {
  return { ...renderCounts };
}

export function getWatchdogStatus(): { blocked: boolean; blockCount: number } {
  return {
    blocked: watchdogBlockedCount > 0,
    blockCount: watchdogBlockedCount,
  };
}

export function getDebugPayload(): Record<string, unknown> {
  return {
    timestamp: new Date().toISOString(),
    events: getDebugEvents(),
    renderCounts: getRenderCounts(),
    watchdog: getWatchdogStatus(),
  };
}

export function copyDebugPayload(): void {
  if (!debugEnabled()) return;
  const payload = JSON.stringify(getDebugPayload(), null, 2);
  navigator.clipboard.writeText(payload).then(() => {
    console.info("[debug] Payload copied to clipboard");
    recordDebugEvent("debug", "Payload copied to clipboard");
  });
}
